/**
 * Browse by activity — tab nav construction, deferred panel loading, per-panel Swiper.
 *
 * Moved verbatim out of the inline <script> in sections/browse-by-activity.liquid.
 * The three Liquid interpolations it used are now data attributes on the section root:
 *
 *   data-bba-tabs            marker — every matching element on the page is booted
 *   data-bba-section-id      {{ section.id }} — generates the `BbaTabLine-<id>-<i>` tab ids
 *   data-bba-tab-link-gap    {{ section.settings.tab_link_gap | default: 16 }} —
 *                            the nav swiper's >=750px `spaceBetween`
 */
(function () {
  // The section emits this tag once per instance, so the browser may execute the file
  // more than once on a page. Everything below (including the shopify:section:load
  // registration) runs exactly once; boot() picks up every instance.
  if (window.__browseByActivityTabs) return;
  window.__browseByActivityTabs = true;

  function initTabs(section) {
    if (!section) return;
    if (section.dataset.bbaTabsInit === 'true') return;
    section.dataset.bbaTabsInit = 'true';

    const sectionId = section.getAttribute('data-bba-section-id') || '';
    const gapAttr = parseInt(section.getAttribute('data-bba-tab-link-gap'), 10);
    const navBreakpointGap = isNaN(gapAttr) ? 16 : gapAttr;

    const tabsNavWrap = section.querySelector('.tabs-nav-swiper');
    const tabsNav = section.querySelector('.bba-tabs-nav');
    const contentItems = section.querySelectorAll('.bba-tabs-content__item');

    tabsNav.innerHTML = '';
    contentItems.forEach((item, index) => {
      const label = item.getAttribute('data-tab-label') || 'Tab';
      const link = item.getAttribute('data-tab-link') || '#';
      const tabId = item.id;
      const li = document.createElement('li');
      li.className = 'swiper-slide';
      li.setAttribute('role', 'presentation');

      const btn = document.createElement('a');
      btn.className = 'bba-link' + (index === 0 ? ' is-active' : '');
      btn.id = `BbaTabLine-${sectionId}-${index}`;
      btn.setAttribute('data-tab-id', tabId);
      btn.setAttribute('href', link);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      btn.setAttribute('aria-controls', tabId);
      btn.setAttribute('aria-label', `View ${label} activity`);
      btn.textContent = label;
      item.setAttribute('aria-labelledby', btn.id);

      li.appendChild(btn);
      tabsNav.appendChild(li);
    });

    function setNavSwiperWidth() {
      const rect = tabsNavWrap.getBoundingClientRect();
      tabsNavWrap.style.width = (window.innerWidth - rect.left) + 'px';
    }

    // The tab list itself is a carousel once there are more than four tabs. It is built
    // as soon as the library lands; until then the tabs are already in the DOM and
    // clickable, they just do not scroll yet.
    let navSwiper = null;
    if (contentItems.length > 4) {
      window.loadSwiper().then(buildNavSwiper).catch(useGridNav);
    } else {
      useGridNav();
    }

    function buildNavSwiper() {
      navSwiper = new Swiper(tabsNavWrap, {
        slidesPerView: 'auto',
        spaceBetween: 32,
        allowTouchMove: true,
        slidesOffsetAfter: 16,
        freeMode: true,
        observer: false,
        observeParents: false,
        resizeObserver: false,
        on: {
          init: function() {
            setNavSwiperWidth();
          },
          resize: function() {
            requestAnimationFrame(function () {
              tabsNavWrap.style.width = '';
              setNavSwiperWidth();
            });
          }
        },
        breakpoints: {
          750: {
            spaceBetween: navBreakpointGap,
            slidesOffsetAfter: 8,
          },
        }
      });

      tabsNavWrap.classList.remove('is-grid');
      tabsNavWrap.classList.add('swiper');
    }

    function useGridNav() {
      tabsNavWrap.classList.add('is-grid');
      tabsNavWrap.classList.remove('swiper');
      tabsNav.querySelectorAll('li').forEach(li => {
        li.classList.remove('swiper-slide');
        const btn = li.querySelector('.bba-link');
        if(btn) {
          btn.style.width = 'auto';
          btn.style.marginRight = '0';
        }
      });
    }

    if (contentItems.length > 0) {
      contentItems[0].classList.add('is-active');
      if ('IntersectionObserver' in window) {
        // Only the visible tab's Swiper is built, and only once the section is
        // near the viewport. Disconnect only when the init actually succeeded —
        // a false return means the panel still needs Swiper, so fetch it and retry.
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            if (activateTabContent(contentItems[0])) {
              observer.disconnect();
              return;
            }
            window.loadSwiper().then(() => {
              if (activateTabContent(contentItems[0])) observer.disconnect();
            }).catch(() => {});
          });
        }, { rootMargin: '200px 0px' });
        observer.observe(section);
      } else if (!activateTabContent(contentItems[0])) {
        window.loadSwiper().then(() => activateTabContent(contentItems[0])).catch(() => {});
      }
    }

    function commitTabSelection(nav, activeContent) {
      tabsNav.querySelectorAll('.bba-link').forEach(n => {
        n.classList.remove('is-active');
        n.setAttribute('aria-selected', 'false');
      });
      contentItems.forEach(c => c.classList.remove('is-active'));

      nav.classList.add('is-active');
      nav.setAttribute('aria-selected', 'true');
      activeContent.classList.add('is-active');
      if (!activateTabContent(activeContent)) {
        window.loadSwiper().then(() => activateTabContent(activeContent)).catch(() => {});
      }

      if (!navSwiper) return;

      const containerWidth = tabsNavWrap.offsetWidth;
      const slideLeft = nav.offsetLeft;
      const slideRight = slideLeft + nav.offsetWidth;
      const currentTranslate = Math.abs(navSwiper.translate);
      const buffer = 50;
      const index = Array.from(tabsNav.querySelectorAll('.bba-link')).indexOf(nav);

      if (window.innerWidth >= 750) {
        if (slideRight > (currentTranslate + containerWidth - buffer)) {
          navSwiper.slideTo(index);
        } else if (slideLeft < (currentTranslate + buffer)) {
          navSwiper.slideTo(index > 0 ? index - 1 : 0);
        }
      }
    }

    tabsNav.querySelectorAll('.bba-link').forEach(nav => {
      nav.addEventListener('click', (e) => {
        if (nav.classList.contains('is-active')) {
           return;
        }

        e.preventDefault();
        const tabId = nav.getAttribute('data-tab-id');
        const activeContent = section.querySelector(`#${tabId}`);
        if (!activeContent) return;

        // Last click wins if the visitor selects another tab before this one is ready.
        section.dataset.pendingTabId = tabId;
        const deferred = activeContent.querySelector('[data-bba-deferred]');
        if (deferred && deferred.getAttribute('data-bba-state') !== 'loaded') {
          nav.setAttribute('aria-busy', 'true');
          loadDeferredPanel(activeContent, deferred).then(() => {
            nav.removeAttribute('aria-busy');
            if (section.dataset.pendingTabId !== tabId) return;
            commitTabSelection(nav, activeContent);
          });
          return;
        }

        commitTabSelection(nav, activeContent);
      });
    });
  }

  // Activate a panel only after a deferred panel has its cards. Returns false only when
  // the panel is not ready yet or Swiper still needs a retry.
  function activateTabContent(container) {
    const deferred = container.querySelector('[data-bba-deferred]');
    if (deferred && deferred.getAttribute('data-bba-state') !== 'loaded') {
      return false;
    }
    return initTabSwiper(container);
  }

  // Fetch a tab's product cards from the shared deferred-product-grid contract:
  // GET <collection.url>?view=deferred-grid -> fragment containing
  // <div class="deferred-grid" data-collection="<handle>"> with product cards.
  function loadDeferredPanel(container, panel) {
    const state = panel.getAttribute('data-bba-state');
    if (state === 'loaded') return Promise.resolve(true);
    if (panel._bbaDeferredLoadPromise) return panel._bbaDeferredLoadPromise;

    const url = panel.getAttribute('data-deferred-collection-url');
    const track = panel.querySelector('.activity-tabs-slider-track');
    const swiperContainer = panel.querySelector('.activity-tabs-swiper');
    const status = panel.querySelector('[data-bba-deferred-status]');
    if (!url || !track || !swiperContainer) return Promise.resolve(false);

    panel.setAttribute('data-bba-state', 'loading');

    const deferredUrl = new URL(url, window.location.origin);
    deferredUrl.searchParams.set('view', 'deferred-grid');

    panel._bbaDeferredLoadPromise = fetch(deferredUrl.toString())
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('.deferred-grid');
        if (!grid) throw new Error('No .deferred-grid in deferred-product-grid response');

        let items = Array.from(grid.querySelectorAll(':scope > .deferred-grid__item'));
        if (items.length === 0) items = Array.from(grid.children);

        // Match the original Liquid loop exactly: it limits the collection to its first
        // six positions and only then removes unavailable products. The endpoint retains
        // unavailable position markers so a later available product cannot move into the row.
        items = items
          .slice(0, 6)
          .filter((item) => item.getAttribute('data-product-available') !== 'false');

        if (items.length === 0) throw new Error('Empty .deferred-grid in deferred-product-grid response');

        const fragment = document.createDocumentFragment();
        items.forEach((item) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide js-magnetic-item activity-slider-item activity-tabs-slider-item';
          // Unwrap .deferred-grid__item so a fetched slide holds exactly what a
          // server-rendered slide holds (the card plus its style snippets).
          if (item.classList.contains('deferred-grid__item')) {
            while (item.firstChild) slide.appendChild(item.firstChild);
          } else {
            slide.appendChild(item);
          }
          fragment.appendChild(slide);
        });
        track.appendChild(fragment);

        panel.setAttribute('data-bba-state', 'loaded');
        swiperContainer.removeAttribute('aria-busy');
        if (status) status.remove();

        // Injected cards carry no inline bootstrap script (product-card scripts
        // are deduplicated); re-run the shared swatch initialiser for them.
        window.ProductColorSwatches?.refresh?.(panel);

        // A panel the visitor has already navigated away from has no layout;
        // its Swiper is built on the next activation instead.
        if (container.classList.contains('is-active') && !initTabSwiper(container)) {
          window.loadSwiper().then(() => initTabSwiper(container)).catch(() => {});
        }
        return true;
      })
      .catch((error) => {
        console.error('Browse by activity: deferred tab failed', error);
        panel.setAttribute('data-bba-state', 'error');
        swiperContainer.removeAttribute('aria-busy');
        return false;
      })
      .finally(() => {
        panel._bbaDeferredLoadPromise = null;
      });

    return panel._bbaDeferredLoadPromise;
  }

  // Returns true when the panel's carousel is ready (or has nothing to build),
  // false only when Swiper itself is not available yet.
  function initTabSwiper(container) {
    const swiperContainer = container.querySelector('.activity-tabs-swiper');
    if (!swiperContainer) return true;
    if (swiperContainer.swiper) return true; // Already initialized

    const slides = swiperContainer.querySelectorAll('.activity-tabs-slider-item');
    if (slides.length === 0) return true;

    const colsDesktop = parseInt(swiperContainer.getAttribute('data-columns')) || 3;
    const colsMobile = parseInt(swiperContainer.getAttribute('data-mobile-columns')) || 1;
    const gap = 16;

    const hasEnoughForLoop = slides.length > colsDesktop;

    function setSwiperWidth() {
      if (window.innerWidth >= 750) {
        const rect = swiperContainer.getBoundingClientRect();
        swiperContainer.style.width = (window.innerWidth - rect.left) + 'px';
      }
    }
    setSwiperWidth();

    // Library not in yet — the caller fetches it via loadSwiper() and calls back.
    if (!window.Swiper) return false;

    new Swiper(swiperContainer, {
      slidesPerView: 'auto',
      spaceBetween: gap,
      slidesOffsetAfter: 0,
      observer: false,
      observeParents: false,
      resizeObserver: false,
      navigation: {
        nextEl: container.querySelector('.activity-tabs-slider-next'),
        prevEl: container.querySelector('.activity-tabs-slider-prev'),
      },
      pagination: {
        el: container.querySelector('.swiper-pagination'),
        type: 'progressbar',
      },
      breakpoints: {
        0: {
          allowTouchMove: true,
        },
        750: {
          allowTouchMove: false,
          spaceBetween: gap,
          slidesOffsetAfter: 16,
        },
      },

      loop: false,
      on: {
        init: function () {
          const isMobile = window.innerWidth <= 749;
          const outerArrows = swiperContainer.querySelector(':scope > slideshow-arrows');
          if (isMobile && outerArrows) outerArrows.style.display = 'none';
          requestAnimationFrame(setArrowPosition);
        },
        resize: function () {
          const currentIndex = this.activeIndex;
          const isMobile = window.innerWidth <= 749;
          const outerArrows = swiperContainer.querySelector(':scope > slideshow-arrows');
          if (isMobile && outerArrows) outerArrows.style.display = 'none';
          else if (outerArrows) outerArrows.style.display = 'flex';
          if (window.innerWidth >= 750) {
            swiperContainer.style.width = '';
            setSwiperWidth();
          } else {
            swiperContainer.style.width = '';
          }
          requestAnimationFrame(setArrowPosition);
          this.slideTo(currentIndex, 0, false);
        }
      }
    });

    // Prevent inner product-card image gallery drag/touch events from bubbling
    // to the outer Swiper, and enable mouse drag-to-scroll on the inner gallery.
    // (Inner slideshow-component is disabled by slideshow.js isNested detection
    //  so we handle drag manually here.)
    function setupInnerSliderDrag() {
      const innerScrollers = swiperContainer.querySelectorAll('.product-card-images-slider slideshow-slides');
      innerScrollers.forEach(function(scroller) {
        if (scroller.dataset.bbaDragInit) return;
        scroller.dataset.bbaDragInit = '1';

        var isDown = false;
        var startX = 0;
        var scrollLeft = 0;
        var moved = false;

        scroller.addEventListener('mousedown', function(e) {
          if (e.button !== 0) return;
          e.stopPropagation();
          isDown = true;
          moved = false;
          startX = e.clientX;
          scrollLeft = scroller.scrollLeft;
          scroller.style.cursor = 'grabbing';
          scroller.style.userSelect = 'none';
        });

        document.addEventListener('mousemove', function(e) {
          if (!isDown) return;
          var dx = e.clientX - startX;
          if (Math.abs(dx) > 3) moved = true;
          if (!moved) return;
          e.preventDefault();
          scroller.scrollLeft = scrollLeft - dx;
        });

        document.addEventListener('mouseup', function() {
          if (!isDown) return;
          isDown = false;
          scroller.style.cursor = '';
          scroller.style.userSelect = '';
          if (moved) {
            moved = false;
            document.addEventListener('click', function stopClick(ev) {
              ev.stopPropagation();
              ev.preventDefault();
              document.removeEventListener('click', stopClick, true);
            }, { capture: true, once: true });
          }
        });



        // Block touch/pointer propagation to parent Swiper on desktop only.
        // On mobile the inner gallery is CSS-locked (overflow-x: hidden),
        // so we let events propagate through to the outer activity-tabs-swiper card carousel.
        ['pointerdown', 'touchstart'].forEach(function(evtName) {
          scroller.addEventListener(evtName, function(e) {
            if (window.innerWidth >= 750) {
              e.stopPropagation();
            }
          }, { passive: true });
        });
      });
    }
    setupInnerSliderDrag();

    // Position arrows at the vertical center of the product card image
    function setArrowPosition() {
      const firstImg = swiperContainer.querySelector('.image-block__image, .product-media__image');
      if (firstImg) {
        const imgH = firstImg.offsetHeight;
        swiperContainer.style.setProperty('--arrow-top', (imgH / 2) + 'px');
      }
    }

    return true;
  }

  // Boot every section root in `scope` (the document on load, the re-injected
  // section element in the theme editor). initTabs latches per root.
  function boot(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches('[data-bba-tabs]')) initTabs(scope);
    scope.querySelectorAll('[data-bba-tabs]').forEach((root) => initTabs(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      boot(event.target);
    });
  }
})();
